import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDateToIndonesian(date: Date): string {
  if (!date) return "-";
  if (isNaN(date.getTime())) {
    return "-";
  }

  const formatter = new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

  return formatter.format(date);
}

type DateFormat = "full" | "date" | "time" | "short" | "long" | "medium";

export const formatDateIndonesia = (
  dateString: string,
  format: DateFormat = "full",
): string => {
  if (!dateString) return "-";

  const locale = "id-ID";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    // fallback kalau date tidak valid
    return "-";
  }

  const formatters = {
    full: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    long: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    medium: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    date: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    }),
    short: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };

  if (format == "full") return `${formatters[format].format(date)} WITA`;

  return formatters[format].format(date);
};

export const formatDate = (
  dateString: string,
  format: DateFormat = "full",
  locale: string,
): string => {
  if (!dateString) return "-";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    // fallback kalau date tidak valid
    return "-";
  }

  const formatters = {
    full: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    long: new Intl.DateTimeFormat(locale, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    medium: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    date: new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    time: new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    }),
    short: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };

  if (format == "full") return `${formatters[format].format(date)} WITA`;

  return formatters[format].format(date);
};

export const getInitials = (name: string) => {
  if (!name) return "";
  const nameParts = name.split(" ");
  const initials = nameParts.map((part) => part[0]).join("");
  return initials.toUpperCase();
};

export function formatRupiah(amount: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(amount);
}

export const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export const firstCapital = (text: string) => {
  if (!text) return text;
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// Helper untuk mengecek kecerahan warna (HEX)
export function getBrightness(hexColor: string) {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

// === FUNGSI BARU: Konversi HEX ke format HSL Shadcn (H S% L%) ===
export function hexToHsl(hex: string): string {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function setGlobalColorTheme(color: string) {
  const root = document.documentElement;
  const hslValue = hexToHsl(color); // Konversi HEX ke HSL

  // 1. Set Primary & Ring (Format HSL)
  root.style.setProperty("--primary", hslValue);
  root.style.setProperty("--ring", hslValue);

  // 2. Set Sidebar Brand Color
  root.style.setProperty("--sidebar-primary", hslValue);

  // 3. Hitung Kontras Text Primary (Gunakan nilai HSL Shadcn agar konsisten)
  if (getBrightness(color) > 125) {
    root.style.setProperty("--primary-foreground", "222.2 47.4% 11.2%"); // Teks gelap
    root.style.setProperty("--sidebar-primary-foreground", "222.2 47.4% 11.2%");
  } else {
    root.style.setProperty("--primary-foreground", "210 40% 98%"); // Teks terang
    root.style.setProperty("--sidebar-primary-foreground", "210 40% 98%");
  }
}

export function setSecondaryTheme(color: string) {
  const root = document.documentElement;
  const hslValue = hexToHsl(color);

  root.style.setProperty("--secondary", hslValue);

  if (getBrightness(color) > 125) {
    root.style.setProperty("--secondary-foreground", "222.2 47.4% 11.2%");
  } else {
    root.style.setProperty("--secondary-foreground", "210 40% 98%");
  }
}

export function setSidebarTheme(color: string) {
  const root = document.documentElement;

  // 1. Set Background Sidebar
  root.style.setProperty("--sidebar", color);

  // 2. Hitung Kontras untuk Text & Border Sidebar
  const brightness = getBrightness(color);

  if (brightness > 125) {
    // === JIKA SIDEBAR TERANG (LIGHT) ===
    root.style.setProperty("--sidebar-foreground", "oklch(0.205 0 0)");

    root.style.setProperty(
      "--sidebar-accent",
      `color-mix(in srgb, ${color}, black 7%)`,
    );
    root.style.setProperty("--sidebar-accent-foreground", "oklch(0.205 0 0)");

    root.style.setProperty(
      "--sidebar-border",
      `color-mix(in srgb, ${color}, black 10%)`,
    );
    root.style.setProperty(
      "--sidebar-ring",
      `color-mix(in srgb, ${color}, black 20%)`,
    );
  } else {
    // === JIKA SIDEBAR GELAP (DARK) ===
    root.style.setProperty("--sidebar-foreground", "oklch(0.985 0 0)");

    root.style.setProperty(
      "--sidebar-accent",
      `color-mix(in srgb, ${color}, white 10%)`,
    );
    root.style.setProperty("--sidebar-accent-foreground", "oklch(0.985 0 0)");

    root.style.setProperty(
      "--sidebar-border",
      `color-mix(in srgb, ${color}, white 10%)`,
    );
    root.style.setProperty(
      "--sidebar-ring",
      `color-mix(in srgb, ${color}, white 20%)`,
    );
  }
}

export const getPaymentStatusInfo = (status: number) => {
  switch (status) {
    case 1:
      return {
        label: "Menunggu Pembayaran",
        // Warna Amber/Kuning untuk status Pending
        className:
          "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50",
      };
    case 2:
      return {
        label: "Lunas",
        // Warna Emerald/Hijau untuk status Berhasil
        className:
          "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800/50",
      };
    case 3:
      return {
        label: "Expired / Batal",
        // Warna Rose/Merah untuk status Gagal/Expired
        className:
          "bg-rose-100 text-rose-800 border-rose-200 hover:bg-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800/50",
      };
    case 4:
      return {
        label: "Ditunda (Sudah Bayar)",
        // Warna Biru untuk status tertunda namun uang sudah masuk
        className:
          "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50",
      };
    case 5:
      return {
        label: "Beasiswa",
        // Warna Ungu/Indigo untuk jalur khusus (Beasiswa)
        className:
          "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800/50",
      };
    default:
      return {
        label: "Status Tidak Diketahui",
        // Warna Abu-abu default
        className:
          "bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700",
      };
  }
};
